import React, { useEffect, useRef, useState } from 'react';
import './LiquidLightToggle.css';

interface CardData {
    id: 'cosmetic' | 'orthodontic' | 'restorative';
    label: string;
    desc: React.ReactNode;
    image: string;
    initialStatus: string;
    finalStatus: string;
}

const cards: CardData[] = [
    {
        id: 'cosmetic',
        label: 'Aesthetic Engine',
        desc: (
            <>
                Visualizing and simulating the perfect and <span className="highlight">clinically achievable</span> smile result. The AI generates a natural texture prediction, not a flat overlay.
            </>
        ),
        image: '/images/Use%20cases/cosmetic.jpg',
        initialStatus: "RENDERING TEXTURE...",
        finalStatus: "CLINICALLY VERIFIED"
    },
    {
        id: 'orthodontic',
        label: 'Alignment Prediction',
        desc: (
            <>
                Simulating the final biological outcome of tooth movement. The algorithm predicts the <span className="highlight">exact tissue response</span> and final occlusion, ensuring a natural, stable result.
            </>
        ),
        image: '/images/Use%20cases/orthodontist.jpg',
        initialStatus: "SIMULATING MOVEMENT...",
        finalStatus: "CLINICALLY VERIFIED"
    },
    {
        id: 'restorative',
        label: 'Reconstruction Sim',
        desc: (
            <>
                Visualizing the integration of implants and prosthetics. The engine renders how light interacts with materials to guarantee a <span className="highlight">hyper-realistic</span> prediction of the new smile.
            </>
        ),
        image: '/images/Use%20cases/Restorative.jpg',
        initialStatus: "ANALYZING STRUCTURE...",
        finalStatus: "CLINICALLY VERIFIED"
    }
];

const LiquidLightToggle: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'cosmetic' | 'orthodontic' | 'restorative'>('cosmetic');
    const [pillStyle, setPillStyle] = useState({});
    const [statusStyles, setStatusStyles] = useState<{ [key: string]: { status: 'loading' | 'success', text: string } }>({
        cosmetic: { status: 'loading', text: "RENDERING TEXTURE..." },
        orthodontic: { status: 'loading', text: "SIMULATING MOVEMENT..." },
        restorative: { status: 'loading', text: "ANALYZING STRUCTURE..." }
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const buttonsRef = useRef<{ [key: string]: HTMLButtonElement | null }>({});

    // Position the pill
    useEffect(() => {
        const container = containerRef.current;
        const currentBtn = buttonsRef.current[activeTab];

        if (container && currentBtn) {
            const containerRect = container.getBoundingClientRect();
            const rect = currentBtn.getBoundingClientRect();
            const left = rect.left - containerRect.left;
            const width = rect.width;

            // Simple animation for pill movement
            // Identify direction and distance for squash/stretch effect (simplified from original JS)
            // But we can just set the style and let CSS transition handle it as per original CSS

            setPillStyle({
                width: `${width}px`,
                left: `${left}px`,
                transform: 'scale(1, 1)' // Reset scale
            });

            // Simulate squash/stretch using a temporary scale override?
            // The original JS calculated distance. 
            // For simplicity and React declarative nature, we let the CSS transition do most work.
            // If we want the exact "squash" effect, we'd need previous index.
        }
    }, [activeTab]);

    // Enhanced effect to mimic the JS squash implementation
    const [prevIndex, setPrevIndex] = useState(0);
    useEffect(() => {
        const tabs = ['cosmetic', 'orthodontic', 'restorative'];
        const limit = tabs.indexOf(activeTab);
        const distance = Math.abs(limit - prevIndex);

        if (distance > 0) {
            const scaleX = 1 + (0.15 * distance);
            const scaleY = 1 - (0.1 * distance);

            // Apply stretch
            setPillStyle(prev => ({ ...prev, transform: `scale(${scaleX}, ${scaleY})` }));

            // Reset after animation
            const timer = setTimeout(() => {
                setPillStyle(prev => ({ ...prev, transform: `scale(1, 1)` }));
            }, 250);

            return () => clearTimeout(timer);
        }

        setPrevIndex(limit);
    }, [activeTab]); // Warning: this depends on pillStyle update logic which is also in previous effect. 
    // Merging logic might be cleaner but let's see. 
    // Actually, the first useEffect sets the base position. This one adds the squish.

    // Trigger AI Sequence logic
    useEffect(() => {
        // Reset to loading state for the active tab
        const currentCard = cards.find(c => c.id === activeTab);
        if (!currentCard) return;

        setStatusStyles(prev => ({
            ...prev,
            [activeTab]: { status: 'loading', text: currentCard.initialStatus }
        }));

        const timer = setTimeout(() => {
            setStatusStyles(prev => ({
                ...prev,
                [activeTab]: { status: 'success', text: currentCard.finalStatus }
            }));
        }, 1200);

        return () => clearTimeout(timer);
    }, [activeTab]);


    return (
        <div className="liquid-toggle-root">
            <div className="toggle-wrapper">
                <div className="toggle-container" ref={containerRef} id="toggleContainer">
                    <div
                        className="active-pill"
                        id="activePill"
                        style={pillStyle}
                    ></div>

                    {cards.map((card) => (
                        <button
                            key={card.id}
                            ref={el => { buttonsRef.current[card.id] = el }}
                            className={`toggle-btn ${activeTab === card.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(card.id)}
                        >
                            {card.id.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* Contextual Reveal Stage */}
            <div className="content-stage">
                {cards.map((card) => (
                    <div
                        key={card.id}
                        className={`content-card ${activeTab === card.id ? 'visible' : ''}`}
                        id={`card-${card.id}`}
                        style={{ overflow: 'hidden' }}
                    >
                        <div style={{ position: 'absolute', inset: 0, zIndex: -1 }}>
                            <img
                                src={card.image}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4 }}
                            />
                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.4))' }} />
                        </div>
                        <div className="card-label">{card.label}</div>
                        <p className="card-desc">
                            {card.desc}
                        </p>
                        <div className="ai-badge" id={`ai-badge-${card.id}`}>
                            <div className={`status-dot ${statusStyles[card.id]?.status === 'success' ? 'success' : ''}`}></div>
                            <span className="badge-text" style={{ color: statusStyles[card.id]?.status === 'success' ? '#ffffff' : '#a3a3a3' }}>
                                {statusStyles[card.id]?.text}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LiquidLightToggle;
